import { type ComponentRef, useEffect, useRef } from "react";
import { Platform } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { Autocomplete } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";
import { matchesTimeZone, timeZoneLabel, timeZoneOptions, type DefaultTimeZone } from "@visioni/a3s-monitoring/app";

interface ZoneOption {
  readonly id: string;
  /** In the list: `Perth, Australia — UTC+08:00`. */
  readonly label: string;
  /** Once selected: the label, except Default, which then says which default and which zone it is. */
  readonly chip: string;
  readonly group: string;
  /** The zone choosing this option applies; `undefined` for Default while it is still loading. */
  readonly timeZone: string | undefined;
}

const DEFAULT_ID = "default";

interface TimeZoneFieldProps {
  /** The zone applied. */
  readonly value: string;
  readonly fallback: DefaultTimeZone | undefined;
  /** When zones are named for — the start of the range, so a July search shows AEST rather than AEDT. */
  readonly at: Date;
  readonly onChange: (timeZone: string) => void;
  /** Whether the list filters by site, so Default can be a site's zone; the fleet list cannot. */
  readonly hasSiteFilter: boolean;
}

const optionId = (o: ZoneOption) => o.id;
const optionChip = (o: ZoneOption) => o.chip;
const optionGroup = (o: ZoneOption) => o.group;

/**
 * Every timezone: Default and then Australia and New Zealand suggested, then
 * the full list — each named by place and country, with its offset.
 *
 * Default is shown as selected whenever the applied zone *is* the default —
 * including a zone that arrived in a link — so it follows the site from then
 * on; any other zone is an explicit choice and stays put.
 */
export function TimeZoneField({ value, fallback, at, onChange, hasSiteFilter }: TimeZoneFieldProps) {
  const fallbackZone = fallback === undefined ? undefined : timeZoneLabel(fallback.timeZone, at);
  const defaultOption: ZoneOption = {
    id: DEFAULT_ID,
    label: hasSiteFilter ? "Default — site timezone, else user timezone" : "Default — user timezone",
    // The chip says which default applies, since the list option cannot know ahead of time.
    chip:
      fallback === undefined || fallbackZone === undefined ?
        "Default"
      : `Default (${fallback.source}): ${fallbackZone}`,
    // First under Suggested, directly ahead of the suggested zones, so the two share one heading.
    group: "Suggested",
    timeZone: fallback?.timeZone,
  };
  const zones = timeZoneOptions(
    Intl.supportedValuesOf("timeZone"),
    [value, ...(fallback === undefined ? [] : [fallback.timeZone])],
    at,
  ).map(
    (o): ZoneOption => ({
      // A suggested zone is listed twice, so the id carries its section.
      id: `${o.group}:${o.timeZone}`,
      label: o.label,
      chip: o.label,
      group: o.group,
      timeZone: o.timeZone,
    }),
  );
  const selected =
    value === fallback?.timeZone ?
      defaultOption
    : zones.find((o) => o.timeZone === value && o.group === "All time zones");

  // Matches a zone by its IANA id as well as what its label shows, so "Australia/Perth", "Perth" and "AWST" all find it.
  const filterOptions = (options: readonly ZoneOption[], input: string) =>
    input.trim() === "" ?
      options
    : options.filter((o) =>
        o.timeZone === undefined || o.id === DEFAULT_ID ?
          o.label.toLowerCase().includes(input.trim().toLowerCase())
        : matchesTimeZone(o.timeZone, at, input),
      );

  return (
    <Autocomplete<ZoneOption>
      options={[defaultOption, ...zones]}
      value={selected ?? null}
      // Clearing the field returns it to Default rather than leaving no zone.
      onChange={(option) => {
        const next = option === null ? fallback?.timeZone : option.timeZone;
        if (next !== undefined) onChange(next);
      }}
      getOptionLabel={optionChip}
      getOptionId={optionId}
      groupBy={optionGroup}
      filterOptions={filterOptions}
      renderOption={(option, state) => <ZoneOptionRow label={option.label} focused={state.focused} />}
      placeholder="Timezone"
      label="Timezone"
      floatLabel="always"
      // "Default (user): Sydney, Australia — UTC+11:00" is cut off at the compact width.
      chipWidth="wide"
    />
  );
}

/**
 * A list row with the full label, styled as `Autocomplete`'s own — which shows
 * the selected value's short label in the field, so the list needs its own row.
 */
function ZoneOptionRow({ label, focused }: { readonly label: string; readonly focused: boolean }) {
  const ref = useRef<ComponentRef<typeof View>>(null);

  // Keeps the row in view as the keyboard moves through the list, as Autocomplete's own rows do.
  useEffect(() => {
    if (focused && Platform.OS === "web" && ref.current) {
      (ref.current as unknown as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [focused]);

  return (
    <View ref={ref} style={[styles.row, focused && styles.rowFocused]}>
      <Text textStyle="sm/regular" color="text" numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  row: { paddingHorizontal: theme.spacing["3xl"], paddingVertical: theme.spacing.xl },
  rowFocused: { backgroundColor: theme.colors.surface2.hover },
}));
